"use client";

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Youtube, X, Send, Clock, AlertCircle, Globe, Lock, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/ui/use-toast";
import {
  useYouTubeAccounts,
  useYouTubeActions,
  type YouTubeAccount,
} from "@/lib/hooks/useYouTube";
import { useLanguageNavigate } from "@/lib/hooks/useLanguageNavigate";

interface YouTubePublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  videoTitle: string;
  onPublished?: () => void;
}

const PRIVACY_OPTIONS = [
  { value: "public", icon: Globe },
  { value: "unlisted", icon: EyeOff },
  { value: "private", icon: Lock },
] as const;

const CATEGORY_OPTIONS = [
  { id: "22", label: "People & Blogs" },
  { id: "26", label: "Howto & Style" },
  { id: "19", label: "Travel & Events" },
  { id: "24", label: "Entertainment" },
  { id: "28", label: "Science & Technology" },
  { id: "17", label: "Sports" },
];

export function YouTubePublishDialog({
  open,
  onOpenChange,
  jobId,
  videoTitle,
  onPublished,
}: YouTubePublishDialogProps) {
  const { t } = useTranslation();
  const navigate = useLanguageNavigate();
  const { data: accountsData, isLoading: accountsLoading } = useYouTubeAccounts();
  const actions = useYouTubeActions();

  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [privacyStatus, setPrivacyStatus] = useState<string>("private");
  const [categoryId, setCategoryId] = useState<string>("22");
  const [isAiGenerated, setIsAiGenerated] = useState(true);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  const accounts = accountsData?.accounts || [];
  const maxTitle = 100;
  const maxDescription = 5000;
  const maxTags = 500;

  // Auto-select first account
  useEffect(() => {
    if (accounts.length > 0 && !selectedAccountId) {
      setSelectedAccountId(accounts[0]!.id);
    }
  }, [accounts, selectedAccountId]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setTitle(videoTitle || "");
      setDescription("");
      setTags([]);
      setTagInput("");
      setPrivacyStatus("private");
      setCategoryId("22");
      setIsAiGenerated(true);
      setIsScheduled(false);
      setScheduledAt("");
      setIsPublishing(false);
    }
  }, [open, videoTitle]);

  const addTag = () => {
    const tag = tagInput.trim().replace(/^#/, "");
    if (!tag) return;
    if (tags.length >= maxTags) return;
    if (tags.includes(tag)) return;
    setTags([...tags, tag]);
    setTagInput("");
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    }
  };

  const handlePublish = async () => {
    if (!selectedAccountId || !jobId || !title.trim()) return;

    setIsPublishing(true);
    try {
      await actions.publish({
        jobId,
        accountId: selectedAccountId,
        title: title.trim(),
        description,
        tags,
        privacyStatus,
        categoryId,
        isAiGenerated,
        ...(isScheduled && scheduledAt ? { scheduledAt: new Date(scheduledAt).toISOString() } : {}),
      });

      toast({
        title: isScheduled ? t("youtube.scheduled") : t("youtube.publishing"),
        description: isScheduled ? t("youtube.scheduledDesc") : t("youtube.publishingDesc"),
      });

      onPublished?.();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: t("youtube.publishError"),
        description: error instanceof Error ? error.message : t("errors.defaultMessage"),
        variant: "destructive",
      });
    } finally {
      setIsPublishing(false);
    }
  };

  // Minimum schedule time: 5 minutes from now
  const minScheduleTime = new Date(Date.now() + 5 * 60 * 1000)
    .toISOString()
    .slice(0, 16);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Youtube className="w-5 h-5 text-red-600" />
            {t("youtube.publishToYouTube")}
          </DialogTitle>
          <DialogDescription>
            {t("youtube.publishDialogDesc", { title: videoTitle })}
          </DialogDescription>
        </DialogHeader>

        {accountsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : accounts.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <AlertCircle className="w-10 h-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground max-w-[220px]">
              {t("youtube.noAccountsConnect")}
            </p>
            <Button
              size="sm"
              className="gap-2 bg-red-600 hover:bg-red-700 text-white"
              onClick={() => {
                onOpenChange(false);
                navigate("/account#youtube");
              }}
            >
              <Youtube className="w-3.5 h-3.5" />
              {t("youtube.connectAccount")}
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Account selector */}
            <div className="space-y-2">
              <Label>{t("youtube.account")}</Label>
              <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("youtube.selectAccount")} />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      <div className="flex items-center gap-2">
                        {account.avatarUrl && !imageErrors[account.id] ? (
                          <img
                            src={account.avatarUrl}
                            alt=""
                            onError={() => setImageErrors((prev) => ({ ...prev, [account.id]: true }))}
                            className="w-5 h-5 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-red-600 flex items-center justify-center">
                            <Youtube className="w-3 h-3 text-white" />
                          </div>
                        )}
                        {account.channelTitle}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>
                  {t("youtube.title")}
                  <span className="text-destructive ml-1">*</span>
                </Label>
                <span className="text-xs text-muted-foreground">
                  {title.length}/{maxTitle}
                </span>
              </div>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, maxTitle))}
                placeholder={t("youtube.titlePlaceholder")}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t("youtube.description")}</Label>
                <span className="text-xs text-muted-foreground">
                  {description.length}/{maxDescription}
                </span>
              </div>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, maxDescription))}
                placeholder={t("youtube.descriptionPlaceholder")}
                rows={3}
                className="resize-none"
              />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t("youtube.tags")}</Label>
                <span className="text-xs text-muted-foreground">
                  {tags.length} {t("youtube.tagsAdded")}
                </span>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder={t("youtube.tagPlaceholder")}
                  className="flex-1 px-3 py-2 text-sm border rounded-md bg-background"
                  disabled={tags.length >= maxTags}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addTag}
                  disabled={!tagInput.trim() || tags.length >= maxTags}
                >
                  {t("youtube.addTag")}
                </Button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="gap-1 cursor-pointer hover:bg-destructive/10"
                      onClick={() => removeTag(tag)}
                    >
                      {tag}
                      <X className="w-3 h-3" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Privacy status */}
            <div className="space-y-2">
              <Label>{t("youtube.privacyStatus")}</Label>
              <Select value={privacyStatus} onValueChange={setPrivacyStatus}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIVACY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex items-center gap-2">
                        <opt.icon className="w-4 h-4" />
                        {t(`youtube.privacy.${opt.value}`)}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label>{t("youtube.category")}</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Content disclosure */}
            <div className="space-y-2">
              <Label>{t("youtube.contentDisclosure")}</Label>
              <div className="space-y-2 pl-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isAiGenerated}
                    onChange={(e) => setIsAiGenerated(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  {t("youtube.aiGenerated")}
                </label>
                <p className="text-xs text-muted-foreground">{t("youtube.aiGeneratedNote")}</p>
              </div>
            </div>

            {/* Schedule toggle */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="youtube-schedule-toggle"
                  checked={isScheduled}
                  onChange={(e) => setIsScheduled(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="youtube-schedule-toggle" className="cursor-pointer">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    {t("youtube.schedulePost")}
                  </div>
                </Label>
              </div>
              {isScheduled && (
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  min={minScheduleTime}
                  className="w-full px-3 py-2 text-sm border rounded-md bg-background"
                />
              )}
              {isScheduled && (
                <p className="text-xs text-muted-foreground">{t("youtube.scheduleNote")}</p>
              )}
            </div>

            {/* Publish button */}
            <Button
              onClick={handlePublish}
              disabled={isPublishing || !selectedAccountId || !title.trim()}
              className="w-full bg-red-600 hover:bg-red-700 text-white gap-2"
            >
              {isPublishing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isScheduled ? (
                <Clock className="w-4 h-4" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {isPublishing
                ? t("youtube.publishingAction")
                : isScheduled
                  ? t("youtube.scheduleAction")
                  : t("youtube.publishAction")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
